import { lazy, Suspense, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FilePlus2,
  ListChecks,
  Receipt,
  Settings,
  Clock,
  SearchCode,
  CloudOff,
  Cloud,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useCatalogos } from '@/hooks/useCatalogos';
import { modoLocal } from '@/db/repositorio';
import { useEnLinea } from '@/hooks/useEnLinea';
import { useActualizacion } from '@/hooks/useActualizacion';
import { Boton, Chip } from '@/components/ui';
import { Bienvenida } from '@/components/Bienvenida';
import { MenuSesion } from '@/components/MenuSesion';
import { useAuth } from '@/hooks/useAuth';

const Acceso = lazy(() => import('@/features/auth/Acceso'));

const NAV = [
  { a: '/', icono: LayoutDashboard, texto: 'Resumen', exacto: true },
  { a: '/guias/nueva', icono: FilePlus2, texto: 'Nueva guía' },
  { a: '/guias', icono: ListChecks, texto: 'Guías', exacto: true },
  { a: '/buscador', icono: SearchCode, texto: 'Buscador' },
  { a: '/estado-pago', icono: Receipt, texto: 'Estado de pago' },
  { a: '/jornada', icono: Clock, texto: 'Horas' },
  { a: '/admin', icono: Settings, texto: 'Administración' },
];

function EstadoConexion() {
  const enLinea = useEnLinea();
  if (modoLocal) {
    return (
      <Chip nivel="AMARILLO" className="whitespace-nowrap">
        Datos en este equipo
      </Chip>
    );
  }
  return enLinea ? (
    <Chip nivel="VERDE" className="whitespace-nowrap">
      <Cloud size={12} /> Sincronizado
    </Chip>
  ) : (
    <Chip nivel="ROJO" className="whitespace-nowrap">
      <CloudOff size={12} /> Sin conexión
    </Chip>
  );
}

export default function App() {
  const { cargando, error } = useCatalogos();
  const { pathname } = useLocation();
  const { hayVersionNueva, actualizar } = useActualizacion();
  const auth = useAuth();
  /** Bloqueo local: sirve incluso sin autenticación, para dejar el equipo cerrado. */
  const [bloqueada, setBloqueada] = useState(false);
  // La apertura se muestra al menos un instante: un parpadeo se ve peor que
  // una transición corta.
  const [tiempoMinimo, setTiempoMinimo] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setTiempoMinimo(false), 1100);
    return () => clearTimeout(t);
  }, []);

  // El acceso se decide solo con la autenticación: los catálogos vienen
  // después, y esperarlos aquí dejaba la apertura colgada cuando Firestore
  // rechazaba la lectura por no haber sesión.
  const resolviendoSesion = auth.cargando || tiempoMinimo;
  const abriendo = resolviendoSesion || cargando;

  if (!resolviendoSesion && (!auth.dentro || bloqueada)) {
    return (
      <Suspense fallback={<Bienvenida visible mensaje="Cargando…" />}>
        <Acceso alDesbloquear={() => setBloqueada(false)} />
      </Suspense>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <Bienvenida
        visible={abriendo}
        mensaje={cargando ? 'Cargando catálogos…' : 'Listo'}
      />

      {/* Encabezado */}
      <header className="sticky top-0 z-20 border-b border-linea bg-petroleo-800/85 backdrop-blur">
        <div className="mx-auto flex w-full max-w-7xl items-center gap-3 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <img
              src="/icons/icon-64.png"
              alt="Xtreme Mining Ltda."
              width={36}
              height={36}
              className="rounded-[9px] shadow-tarjeta ring-1 ring-white/10"
            />
            <div className="leading-tight">
              <div className="text-sm font-semibold">Xtreme Mining</div>
              <div className="hidden text-[11px] text-texto-mute sm:block">
                Control de hormigón · Contrato 4600020915
              </div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <EstadoConexion />
            <MenuSesion alBloquear={() => setBloqueada(true)} />
          </div>
        </div>

        {/* Navegación en escritorio */}
        <nav className="mx-auto hidden w-full max-w-7xl gap-1 px-3 pb-2 md:flex">
          {NAV.map(({ a, icono: Icono, texto, exacto }) => (
            <NavLink
              key={a}
              to={a}
              end={exacto}
              className={({ isActive }) =>
                cn(
                  'inline-flex items-center gap-2 rounded-control px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-petroleo-600 text-texto'
                    : 'text-texto-mute hover:bg-white/5 hover:text-texto',
                )
              }
            >
              <Icono size={16} />
              {texto}
            </NavLink>
          ))}
        </nav>
      </header>

      {/* Aviso de version nueva */}
      {hayVersionNueva && (
        <div className="border-b border-fluor-verde/30 bg-fluor-verde/10">
          <div className="mx-auto flex w-full max-w-7xl flex-wrap items-center gap-3 px-4 py-2">
            <span className="text-xs text-fluor-verde">
              Hay una versión nueva de la aplicación.
            </span>
            <Boton
              variante="exito"
              tamano="sm"
              onClick={() => actualizar?.()}
              className="ml-auto"
            >
              Actualizar ahora
            </Boton>
          </div>
        </div>
      )}

      {/* Contenido */}
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-5 pb-24 md:pb-8">
        {error && (
          <div className="mb-4 rounded-tarjeta border border-fluor-rojo/40 bg-fluor-rojo/10 px-4 py-3 text-sm text-fluor-rojo">
            {error}
          </div>
        )}
        <Outlet />
      </main>

      {/* Navegación inferior en móvil */}
      <nav className="fixed inset-x-0 bottom-0 z-20 border-t border-linea bg-petroleo-800/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden">
        <div className="grid grid-cols-7">
          {NAV.map(({ a, icono: Icono, texto, exacto }) => {
            const activo = exacto ? pathname === a : pathname.startsWith(a);
            return (
              <NavLink
                key={a}
                to={a}
                end={exacto}
                className={cn(
                  'flex min-h-tactil flex-col items-center justify-center gap-1 py-2 text-[10px] leading-none transition-colors',
                  activo ? 'text-fluor-verde' : 'text-texto-mute',
                )}
              >
                <Icono size={19} />
                <span className="px-0.5 text-center">{texto}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
